import { memo } from "react";
import { MetadataItem } from "../types/metadata";
import { ItemCard } from "./ItemCard";
import { ItemGroup } from "./Sidebar";
import Text from "./Text";
import Skeleton from "./Skeleton";
import { useTranslation } from "react-i18next";

interface ItemCardListProps {
  groups: ItemGroup[];
  listContainerRef: React.RefObject<HTMLDivElement | null>;
  cardRefs: React.RefObject<Record<string, HTMLDivElement | null>>;
  onScroll: () => void;
  className?: string;
  onItemSelect: (item: MetadataItem) => void;
  isLoading?: boolean;
  isMobile?: boolean;
}

const ItemCardListInner = ({
  groups,
  listContainerRef,
  cardRefs,
  onScroll,
  className,
  onItemSelect,
  isLoading = false,
  isMobile = false,
}: ItemCardListProps) => {
  // 翻訳インスタンスのインポート
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "ja";

  const SkeletonCard = () => (
    <div className="relative flex flex-col justify-center py-4 pl-4 pr-2 bg-white border-b border-Border min-h-[6rem] min-w-[8rem]">
      <div className="absolute left-0 top-4 bottom-4 w-[4px] bg-gray-200" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-40 max-w-[70%]" />
        <Skeleton className="h-4 w-28 max-w-[55%]" />
      </div>
    </div>
  );

  const SkeletonGroup = ({ index }: { index: number }) => (
    <div key={index} className="relative">
      <div className={`sticky top-0 z-10 ${isMobile ? "bg-white px-0 pt-0 pb-2" : "px-4 py-2"}`}>
        <div
          className={
            isMobile
              ? "flex w-full items-center rounded-xl bg-Background px-4 py-2"
              : "px-4 py-2 border-b-0 border-Border bg-Background rounded-xl"
          }
        >
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
      <div className="pt-2 pb-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  );

  const renderSkeleton = () => (
    <div
      className={`
        overflow-y-auto h-full 
        bg-white 
        [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
        ${className || ""} 
      `}
      ref={listContainerRef}
      onScroll={onScroll}
    >
      {[0, 1].map((index) => (
        <SkeletonGroup key={index} index={index} />
      ))}
    </div>
  );

  if (isLoading) {
    return renderSkeleton();
  }

  return (
    <div
      className={`
        overflow-y-auto h-full 
        bg-white 
        [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
        ${className || ""} 
      `}
      ref={listContainerRef}
      onScroll={onScroll}
    >
      {groups.map((group) => (
        <div key={group.key} className="relative">
          <div
            className="h-0"
            aria-hidden="true"
            ref={(el) => {
              if (!cardRefs?.current) return;
              if (el) {
                cardRefs.current[group.key] = el;
              } else {
                delete cardRefs.current[group.key];
              }
            }}
          />
          <div
            className={`sticky top-0 z-10 ${isMobile ? "bg-white px-0 pt-0 pb-2" : "px-4 py-2"}`}
          >
            {isMobile ? (
              <div className="flex w-full items-center">
                <div className="flex min-w-0 flex-1 items-center rounded-xl bg-Background px-4 py-2">
                  <Text
                    as="h3"
                    variant="body/m"
                    weight="regular"
                    className="shrink-0 whitespace-nowrap"
                  >
                    {t(group.label)}
                  </Text>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-xl border-b-0 border-Border bg-Background px-4 py-2">
                <Text as="h3" variant="body/m" weight="regular" className="max-w-[45%] shrink-0">
                  {t(group.label)}
                </Text>
              </div>
            )}
          </div>
          <div className="pt-2 pb-4">
            {group.items.map((item) => (
              <ItemCard key={item.id} item={item} language={language} onSelect={onItemSelect} />
            ))}
          </div>
        </div>
      ))}
      {groups.length === 0 && (
        <div className="flex justify-center items-start h-full">
          <Text className="p-4 text-TextPrimary text-center">
            {t("条件にあう項目は見つかりませんでした")}
            <br />
            {t("他のキーワードやカテゴリをお試しください")}
          </Text>
        </div>
      )}
    </div>
  );
};

export const ItemCardList = memo(ItemCardListInner);
export default ItemCardList;
